import { Controller } from '@hotwired/stimulus'
import { stimulus } from '~/init'
import * as Turbo from '@hotwired/turbo'

const DEFAULT_SLOT_HEIGHT = 28
const DRAG_THRESHOLD = 5
const NON_DRAGGABLE_STATUSES = ['completed', 'cancelled']

class KanbanBoardController extends Controller {
  static targets = ['nowLine', 'appointment', 'apptCount', 'busyPct']
  static values = {
    dayStart: String,
    dayEnd: String,
    slotCount: Number,
    isToday: Boolean,
    scheduleId: Number,
    newAppointmentUrl: String,
    totalWorkMinutes: Number,
    appointmentCount: Number,
    busyPercent: Number
  }

  connect () {
    this.dragState = null
    this.resizeState = null
    this.activeStatusFilter = null

    this.handleCellClick = (e) => {
      if (e.target.closest('[data-status-picker]')) return
      if (e.target.closest('[data-resize-handle]')) return

      const appointment = e.target.closest('[data-appointment-url]')
      if (appointment) {
        e.stopPropagation()
        this.onAppointmentClick(appointment)
        return
      }

      const cell = e.target.closest('[data-slot-time]')
      if (cell) this.onCellClick(cell)
    }
    this.element.addEventListener('click', this.handleCellClick)

    this.handlePointerDown = (e) => this.onPointerDown(e)
    this.handlePointerMove = (e) => this.onPointerMove(e)
    this.handlePointerUp = (e) => this.onPointerUp(e)
    this.element.addEventListener('pointerdown', this.handlePointerDown)

    this.positionAppointments()

    if (this.isTodayValue && this.hasNowLineTarget) {
      this.updateNowLine()
      this.interval = setInterval(() => this.updateNowLine(), 60000)
    }

    this.resizeObserver = new ResizeObserver(() => {
      if (this.rafId) cancelAnimationFrame(this.rafId)
      this.rafId = requestAnimationFrame(() => this.positionAppointments())
    })
    this.resizeObserver.observe(this.element)
  }

  disconnect () {
    if (this.interval) clearInterval(this.interval)
    if (this.resizeObserver) this.resizeObserver.disconnect()
    if (this.rafId) cancelAnimationFrame(this.rafId)
    this.element.removeEventListener('click', this.handleCellClick)
    this.element.removeEventListener('pointerdown', this.handlePointerDown)
    this.cleanupDrag()
    if (this._resizeMoveHandler) document.removeEventListener('pointermove', this._resizeMoveHandler)
    if (this._resizeUpHandler) document.removeEventListener('pointerup', this._resizeUpHandler)
    this.resizeState = null
  }

  appointmentCountValueChanged () {
    if (this.hasApptCountTarget) {
      this.apptCountTarget.textContent = this.apptCountTarget.textContent.replace(/\d+/, this.appointmentCountValue)
    }
    const legendCount = this.element.querySelector('.schedule-legend-count')
    if (legendCount) legendCount.textContent = legendCount.textContent.replace(/\d+/, this.appointmentCountValue)
  }

  busyPercentValueChanged () {
    if (this.hasBusyPctTarget) {
      this.busyPctTarget.textContent = this.busyPctTarget.textContent.replace(/\d+/, this.busyPercentValue)
    }
  }

  appointmentTargetConnected () {
    if (this.rafId) cancelAnimationFrame(this.rafId)
    this.rafId = requestAnimationFrame(() => {
      this.positionAppointments()
      this.updateStats()
    })
  }

  appointmentTargetDisconnected () {
    if (this.rafId) cancelAnimationFrame(this.rafId)
    this.rafId = requestAnimationFrame(() => {
      this.positionAppointments()
      this.updateStats()
    })
  }

  // --- Appointment clicks ---

  onAppointmentClick (card) {
    const url = card.dataset.appointmentUrl
    if (!url) return

    const drawerFrame = document.querySelector('turbo-frame#drawer')
    if (drawerFrame) {
      drawerFrame.src = url
    }
  }

  // --- Status filter ---

  toggleStatusFilter (e) {
    const btn = e.currentTarget
    const status = btn.dataset.status

    if (this.activeStatusFilter === status) {
      // Same status clicked again — reset to show all
      this.activeStatusFilter = null
    } else {
      this.activeStatusFilter = status
    }

    // Update legend button states
    this.element.querySelectorAll('.schedule-legend-item--filter').forEach(b => {
        if (!this.activeStatusFilter) {
          b.classList.remove('schedule-legend-item--hidden', 'schedule-legend-item--active')
        } else if (b.dataset.status === this.activeStatusFilter) {
          b.classList.remove('schedule-legend-item--hidden')
          b.classList.add('schedule-legend-item--active')
        } else {
          b.classList.add('schedule-legend-item--hidden')
          b.classList.remove('schedule-legend-item--active')
        }
      })

    this.applyStatusFilter()
  }

  applyStatusFilter () {
    if (!this.hasAppointmentTarget) return

    this.appointmentTargets.forEach(card => {
      const status = card.dataset.status
      if (this.activeStatusFilter && status !== this.activeStatusFilter) {
        card.classList.add('kanban-board__appointment--filtered')
      } else {
        card.classList.remove('kanban-board__appointment--filtered')
      }
    })
  }

  // --- Cell clicks (event delegation) ---

  onCellClick (cell) {
    const slotTime = cell.dataset.slotTime
    const mechanicId = cell.dataset.mechanicId

    if (!this.hasNewAppointmentUrlValue) return

    const freeMinutes = this.calcFreeMinutes(mechanicId, slotTime)
    let url = `${this.newAppointmentUrlValue}?mechanic_id=${mechanicId}&starts_at=${slotTime}`
    if (freeMinutes != null && freeMinutes > 0) url += `&free_minutes=${freeMinutes}`

    const drawerFrame = document.querySelector('turbo-frame#drawer')

    if (drawerFrame) {
      drawerFrame.src = url
    }
  }

  // Calculate free minutes from slotTime until next appointment or shift end for given mechanic
  calcFreeMinutes (mechanicId, slotTime) {
    const slotMinutes = this.timeToMinutes(slotTime)

    // Find next appointment start for this mechanic after slotTime
    let nextStart = null
    if (this.hasAppointmentTarget) {
      this.appointmentTargets.forEach(card => {
        if (card.dataset.mechanicId !== mechanicId) return
        const status = card.dataset.status
        if (status === 'cancelled') return
        const start = this.timeToMinutes(card.dataset.startsAt)
        if (start > slotMinutes) {
          if (nextStart === null || start < nextStart) nextStart = start
        }
      })
    }

    // Find shift end for this mechanic from grid cells
    const grid = this.element.querySelector('.kanban-board__grid')
    if (!grid) return null
    const cells = grid.querySelectorAll(`[data-cell-mechanic="${mechanicId}"][data-cell-time]`)
    let shiftEnd = null
    cells.forEach(c => {
      if (c.classList.contains('kanban-board__cell--blocked')) return
      const t = this.timeToMinutes(c.dataset.cellTime)
      if (t >= slotMinutes && (shiftEnd === null || t + 15 > shiftEnd)) {
        shiftEnd = t + 15
      }
    })

    // Also check break: if break starts after slot, it's a boundary
    // Break cells are blocked, so shiftEnd already accounts for contiguous non-blocked cells
    // Find the first blocked cell after slotTime
    let firstBlockedAfter = null
    const sortedCells = Array.from(cells).sort((a, b) =>
      this.timeToMinutes(a.dataset.cellTime) - this.timeToMinutes(b.dataset.cellTime)
    )
    for (const c of sortedCells) {
      const t = this.timeToMinutes(c.dataset.cellTime)
      if (t > slotMinutes && c.classList.contains('kanban-board__cell--blocked')) {
        firstBlockedAfter = t
        break
      }
    }

    const boundary = [nextStart, shiftEnd, firstBlockedAfter].filter(v => v !== null)
    if (boundary.length === 0) return null

    const limit = Math.min(...boundary)
    return Math.max(0, limit - slotMinutes)
  }

  // --- Drag & Drop ---

  onPointerDown (e) {
    if (e.button !== 0) return

    // Resize handle takes priority over drag
    if (e.target.closest('[data-resize-handle]')) {
      this.onResizePointerDown(e)
      return
    }

    const card = e.target.closest('[data-kanban-board-target="appointment"]')
    if (!card) return

    // Don't drag from status picker buttons
    if (e.target.closest('[data-status-picker]')) return

    const status = card.dataset.status
    if (NON_DRAGGABLE_STATUSES.includes(status)) return

    const moveUrl = card.dataset.moveUrl
    if (!moveUrl) return

    const startsAt = card.dataset.startsAt
    const endsAt = card.dataset.endsAt
    const mechanicId = card.dataset.mechanicId

    const startMinutes = this.timeToMinutes(startsAt)
    const endMinutes = this.timeToMinutes(endsAt)
    const durationSlots = Math.max(Math.ceil((endMinutes - startMinutes) / 15), 1)

    this.dragState = {
      card,
      moveUrl,
      startX: e.clientX,
      startY: e.clientY,
      originalMechanicId: mechanicId,
      originalStartsAt: startsAt,
      originalEndsAt: endsAt,
      durationSlots,
      dragStarted: false,
      ghost: null,
      highlightedCells: [],
      targetMechanicId: null,
      targetTime: null,
      isValidDrop: false
    }

    card.setPointerCapture(e.pointerId)
    card.addEventListener('pointermove', this.handlePointerMove)
    card.addEventListener('pointerup', this.handlePointerUp)

    e.preventDefault()
  }

  onPointerMove (e) {
    if (!this.dragState) return

    const { startX, startY } = this.dragState

    if (!this.dragState.dragStarted) {
      const dx = e.clientX - startX
      const dy = e.clientY - startY
      if (Math.sqrt(dx * dx + dy * dy) < DRAG_THRESHOLD) return

      this.startDrag(e)
    }

    this.updateDrag(e)
  }

  onPointerUp (e) {
    if (!this.dragState) return

    const { card } = this.dragState

    card.removeEventListener('pointermove', this.handlePointerMove)
    card.removeEventListener('pointerup', this.handlePointerUp)

    if (!this.dragState.dragStarted) {
      this.dragState = null
      return
    }

    this.finishDrag()
  }

  startDrag (e) {
    const { card } = this.dragState
    this.dragState.dragStarted = true

    // Suppress click event after drag
    this.suppressNextClick = true
    const suppressHandler = (ev) => {
      ev.stopPropagation()
      ev.preventDefault()
      this.element.removeEventListener('click', suppressHandler, true)
      this.suppressNextClick = false
    }
    this.element.addEventListener('click', suppressHandler, true)

    this.dismissAllTooltips()

    // Create ghost
    const ghost = card.cloneNode(true)
    ghost.classList.add('kanban-board__drag-ghost')
    ghost.classList.remove('kanban-board__appointment--positioned')
    ghost.style.visibility = 'visible'
    ghost.style.width = `${card.offsetWidth}px`
    ghost.style.height = `${card.offsetHeight}px`
    ghost.style.left = `${e.clientX - card.offsetWidth / 2}px`
    ghost.style.top = `${e.clientY - 12}px`
    document.body.appendChild(ghost)
    this.dragState.ghost = ghost

    // Visual states
    card.classList.add('kanban-board__appointment--dragging')
    this.element.classList.add('kanban-board--dragging')
  }

  updateDrag (e) {
    const { ghost, durationSlots } = this.dragState
    this.dismissAllTooltips()

    // Move ghost
    ghost.style.left = `${e.clientX - ghost.offsetWidth / 2}px`
    ghost.style.top = `${e.clientY - 12}px`

    // Clear previous highlights
    this.clearHighlights()

    // Find cell under cursor (ghost has pointer-events: none, so elementFromPoint sees cells)
    const el = document.elementFromPoint(e.clientX, e.clientY)
    const cell = el?.closest('[data-cell-mechanic][data-cell-time]')

    if (!cell) {
      this.dragState.targetMechanicId = null
      this.dragState.targetTime = null
      this.dragState.isValidDrop = false
      return
    }

    const targetMechanicId = cell.dataset.cellMechanic
    const targetTime = cell.dataset.cellTime
    this.dragState.targetMechanicId = targetMechanicId
    this.dragState.targetTime = targetTime

    // Highlight N cells down from target
    const grid = this.element.querySelector('.kanban-board__grid')
    const allTimeCells = grid.querySelectorAll(`[data-cell-mechanic="${targetMechanicId}"][data-cell-time]`)
    const timeSlots = Array.from(allTimeCells)
    const startIdx = timeSlots.findIndex(c => c.dataset.cellTime === targetTime)

    if (startIdx === -1) {
      this.dragState.isValidDrop = false
      return
    }

    const cellsToHighlight = []
    for (let i = 0; i < durationSlots && (startIdx + i) < timeSlots.length; i++) {
      cellsToHighlight.push(timeSlots[startIdx + i])
    }

    cellsToHighlight.forEach(c => {
      c.classList.add('kanban-board__cell--drop-target')
      this.dragState.highlightedCells.push(c)
    })

    this.dragState.isValidDrop = true
  }

  finishDrag () {
    const {
      card, ghost, moveUrl, isValidDrop,
      targetMechanicId, targetTime,
      originalMechanicId, originalStartsAt, originalEndsAt, durationSlots
    } = this.dragState

    // Cleanup visual states
    this.clearHighlights()
    if (ghost) ghost.remove()
    card.classList.remove('kanban-board__appointment--dragging')
    this.element.classList.remove('kanban-board--dragging')

    const sameMechanic = targetMechanicId === originalMechanicId
    const sameTime = targetTime === originalStartsAt

    if (!isValidDrop || (sameMechanic && sameTime)) {
      this.dragState = null
      return
    }

    // Optimistic UI: update card data attributes and reposition
    card.dataset.mechanicId = targetMechanicId
    card.dataset.startsAt = targetTime
    card.dataset.endsAt = this.addMinutes(targetTime, durationSlots * 15)
    this.positionAppointments()

    // Send PATCH request
    const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content || ''
    const body = new URLSearchParams()
    body.append('starts_at', targetTime)
    if (targetMechanicId !== originalMechanicId) {
      body.append('user_id', targetMechanicId)
    }

    fetch(moveUrl, {
      method: 'PATCH',
      headers: {
        Accept: 'text/vnd.turbo-stream.html',
        'X-CSRF-Token': csrfToken,
        'Content-Type': 'application/x-www-form-urlencoded'
      },
      body: body.toString()
    }).then(response => {
      if (!response.ok) {
        // Rollback optimistic update
        card.dataset.mechanicId = originalMechanicId
        card.dataset.startsAt = originalStartsAt
        card.dataset.endsAt = originalEndsAt
        this.positionAppointments()
        return
      }
      return response.text()
    }).then(html => {
      if (html) {
        Turbo.renderStreamMessage(html)
      }
    }).catch(() => {
      // Rollback on network error
      card.dataset.mechanicId = originalMechanicId
      card.dataset.startsAt = originalStartsAt
      card.dataset.endsAt = originalEndsAt
      this.positionAppointments()
    })

    this.dragState = null
    requestAnimationFrame(() => this.dismissAllTooltips())
  }

  clearHighlights () {
    if (!this.dragState) return
    this.dragState.highlightedCells.forEach(c => {
      c.classList.remove('kanban-board__cell--drop-target', 'kanban-board__cell--drop-invalid')
    })
    this.dragState.highlightedCells = []
  }

  cleanupDrag () {
    if (!this.dragState) return
    this.clearHighlights()
    if (this.dragState.ghost) this.dragState.ghost.remove()
    if (this.dragState.card) {
      this.dragState.card.classList.remove('kanban-board__appointment--dragging')
    }
    this.element.classList.remove('kanban-board--dragging')
    this.dragState = null
  }

  // --- Resize ---

  onResizePointerDown (e) {
    e.stopPropagation()
    if (e.button !== 0) return

    const card = e.target.closest('[data-kanban-board-target="appointment"]')
    if (!card) return

    const status = card.dataset.status
    if (NON_DRAGGABLE_STATUSES.includes(status)) return

    const resizeUrl = card.dataset.resizeUrl
    if (!resizeUrl) return

    const startsAt = card.dataset.startsAt
    const endsAt = card.dataset.endsAt
    const mechanicId = card.dataset.mechanicId

    const slotHeight = parseInt(getComputedStyle(this.element).getPropertyValue('--kanban-slot-height')) || DEFAULT_SLOT_HEIGHT

    this.dismissAllTooltips()

    this.resizeState = {
      card,
      resizeUrl,
      startY: e.clientY,
      originalEndsAt: endsAt,
      startsAt,
      mechanicId,
      slotHeight,
      newEndsAt: endsAt,
      highlightedCells: []
    }

    this.element.classList.add('kanban-board--resizing')

    this._resizeMoveHandler = (ev) => this.onResizePointerMove(ev)
    this._resizeUpHandler = (ev) => this.onResizePointerUp(ev)
    document.addEventListener('pointermove', this._resizeMoveHandler)
    document.addEventListener('pointerup', this._resizeUpHandler)

    e.preventDefault()
  }

  onResizePointerMove (e) {
    if (!this.resizeState) return
    this.dismissAllTooltips()

    const { startY, originalEndsAt, startsAt, mechanicId, slotHeight, card } = this.resizeState

    const deltaY = e.clientY - startY
    const deltaSlots = Math.round(deltaY / slotHeight)

    const startMinutes = this.timeToMinutes(startsAt)
    const originalEndMinutes = this.timeToMinutes(originalEndsAt)
    const originalSlots = Math.ceil((originalEndMinutes - startMinutes) / 15)

    const grid = this.element.querySelector('.kanban-board__grid')
    const allTimeCells = grid.querySelectorAll(`[data-cell-mechanic="${mechanicId}"][data-cell-time]`)
    const timeSlots = Array.from(allTimeCells)
    const startIdx = timeSlots.findIndex(c => c.dataset.cellTime === startsAt)

    const maxSlots = startIdx !== -1 ? timeSlots.length - startIdx : originalSlots
    const newSlots = Math.min(Math.max(1, originalSlots + deltaSlots), maxSlots)
    const newEndsAt = this.addMinutes(startsAt, newSlots * 15)

    this.resizeState.newEndsAt = newEndsAt

    // Clear previous highlights
    this.clearResizeHighlights()

    // Highlight cells from startsAt to newEndsAt
    if (startIdx !== -1) {
      for (let i = 0; i < newSlots && (startIdx + i) < timeSlots.length; i++) {
        timeSlots[startIdx + i].classList.add('kanban-board__cell--drop-target')
        this.resizeState.highlightedCells.push(timeSlots[startIdx + i])
      }
    }

    // Update card height live
    const startCell = grid.querySelector(`[data-cell-mechanic="${mechanicId}"][data-cell-time="${startsAt}"]`)
    const endCell = grid.querySelector(`[data-cell-mechanic="${mechanicId}"][data-cell-time="${newEndsAt}"]`)

    if (startCell) {
      const startRect = startCell.getBoundingClientRect()
      const endRect = endCell ? endCell.getBoundingClientRect() : null
      let height = endRect ? endRect.top - startRect.top : newSlots * slotHeight
      height = Math.max(height, 22)
      card.style.height = `${height - 2}px`
    }
  }

  onResizePointerUp (e) {
    if (!this.resizeState) return

    document.removeEventListener('pointermove', this._resizeMoveHandler)
    document.removeEventListener('pointerup', this._resizeUpHandler)

    this.clearResizeHighlights()
    this.element.classList.remove('kanban-board--resizing')

    const { card, resizeUrl, originalEndsAt, newEndsAt } = this.resizeState

    if (newEndsAt === originalEndsAt) {
      this.resizeState = null
      requestAnimationFrame(() => this.dismissAllTooltips())
      return
    }

    // Optimistic update
    card.dataset.endsAt = newEndsAt
    this.positionAppointments()
    this.updateStats()

    const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content || ''
    const body = new URLSearchParams()
    body.append('ends_at', newEndsAt)

    fetch(resizeUrl, {
      method: 'PATCH',
      headers: {
        Accept: 'text/vnd.turbo-stream.html',
        'X-CSRF-Token': csrfToken,
        'Content-Type': 'application/x-www-form-urlencoded'
      },
      body: body.toString()
    }).then(response => {
      if (!response.ok) {
        card.dataset.endsAt = originalEndsAt
        this.positionAppointments()
        this.updateStats()
        return
      }
      return response.text()
    }).then(html => {
      if (html) Turbo.renderStreamMessage(html)
    }).catch(() => {
      card.dataset.endsAt = originalEndsAt
      this.positionAppointments()
      this.updateStats()
    })

    this.resizeState = null
    requestAnimationFrame(() => this.dismissAllTooltips())
  }

  clearResizeHighlights () {
    if (!this.resizeState) return
    this.resizeState.highlightedCells.forEach(c => {
      c.classList.remove('kanban-board__cell--drop-target')
    })
    this.resizeState.highlightedCells = []
  }

  dismissAllTooltips () {
    document.querySelectorAll('.tooltip').forEach(t => t.remove())
  }

  updateStats () {
    const totalWork = this.totalWorkMinutesValue
    if (!totalWork) return

    let bookedMinutes = 0
    const cards = this.hasAppointmentTarget ? this.appointmentTargets : []
    cards.forEach(card => {
      const start = this.timeToMinutes(card.dataset.startsAt)
      const end = this.timeToMinutes(card.dataset.endsAt)
      bookedMinutes += end - start
    })

    const percent = totalWork > 0 ? Math.round(bookedMinutes * 100 / totalWork) : 0

    if (this.hasBusyPctTarget) {
      this.busyPctTarget.textContent = this.busyPctTarget.textContent.replace(/\d+/, percent)
    }

    if (this.hasApptCountTarget) {
      this.apptCountTarget.textContent = this.apptCountTarget.textContent.replace(/\d+/, cards.length)
    }

    // Update legend count
    const legendCount = this.element.querySelector('.schedule-legend-count')
    if (legendCount) {
      legendCount.textContent = legendCount.textContent.replace(/\d+/, cards.length)
    }
  }

  addMinutes (timeStr, minutes) {
    const total = this.timeToMinutes(timeStr) + minutes
    const h = Math.floor(total / 60)
    const m = total % 60
    return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`
  }

  // --- Position appointment cards ---

  positionAppointments () {
    if (!this.hasAppointmentTarget) return

    const grid = this.element.querySelector('.kanban-board__grid')
    if (!grid) return

    const gridRect = grid.getBoundingClientRect()
    const boardRect = this.element.getBoundingClientRect()
    const scrollLeft = this.element.scrollLeft
    const scrollTop = this.element.scrollTop

    const gridOffsetTop = gridRect.top - boardRect.top + scrollTop
    const gridOffsetLeft = gridRect.left - boardRect.left + scrollLeft

    const slotHeight = parseInt(getComputedStyle(this.element).getPropertyValue('--kanban-slot-height')) || DEFAULT_SLOT_HEIGHT

    // Batch reads
    const measurements = this.appointmentTargets.map(card => {
      const mechanicId = card.dataset.mechanicId
      const startsAt = card.dataset.startsAt
      const endsAt = card.dataset.endsAt

      const startCell = grid.querySelector(`[data-cell-mechanic="${mechanicId}"][data-cell-time="${startsAt}"]`)
      const endCell = grid.querySelector(`[data-cell-mechanic="${mechanicId}"][data-cell-time="${endsAt}"]`)

      if (!startCell) return { card, visible: false }

      const startRect = startCell.getBoundingClientRect()
      const endRect = endCell ? endCell.getBoundingClientRect() : null

      const startMinutes = this.timeToMinutes(startsAt)
      const endMinutes = this.timeToMinutes(endsAt)
      const durationSlots = Math.max(Math.ceil((endMinutes - startMinutes) / 15), 1)

      let height = endRect ? endRect.top - startRect.top : durationSlots * slotHeight
      height = Math.max(height, 22)

      return {
        card,
        visible: true,
        durationSlots,
        top: startRect.top - gridRect.top + gridOffsetTop,
        left: startRect.left - gridRect.left + gridOffsetLeft + 1,
        width: startRect.width - 2,
        height: height - 2
      }
    })

    // Batch writes
    measurements.forEach(({ card, visible, durationSlots, top, left, width, height }) => {
      if (!visible) {
        card.style.visibility = 'hidden'
        return
      }

      card.classList.remove('kanban-board__appointment--compact', 'kanban-board__appointment--short')
      if (durationSlots <= 1) {
        card.classList.add('kanban-board__appointment--compact')
      } else if (durationSlots <= 2) {
        card.classList.add('kanban-board__appointment--short')
      }

      card.style.top = `${top}px`
      card.style.left = `${left}px`
      card.style.width = `${width}px`
      card.style.height = `${height}px`
      card.classList.add('kanban-board__appointment--positioned')
    })
  }

  timeToMinutes (timeStr) {
    const [h, m] = timeStr.split(':').map(Number)
    return h * 60 + m
  }

  // --- Now line ---

  updateNowLine () {
    const now = new Date()
    const currentMinutes = now.getHours() * 60 + now.getMinutes()

    const [startH, startM] = this.dayStartValue.split(':').map(Number)
    const [endH, endM] = this.dayEndValue.split(':').map(Number)

    const startMinutes = startH * 60 + startM
    const endMinutes = endH * 60 + endM

    if (currentMinutes < startMinutes || currentMinutes > endMinutes) {
      this.nowLineTarget.style.display = 'none'
      return
    }

    const slotHeight = parseInt(
      getComputedStyle(this.element).getPropertyValue('--kanban-slot-height')
    ) || DEFAULT_SLOT_HEIGHT
    const headerRow = this.element.querySelector('.kanban-board__column-header')
    const headerHeight = headerRow ? headerRow.offsetHeight : 0

    const proportion = (currentMinutes - startMinutes) / (endMinutes - startMinutes)
    const totalHeight = this.slotCountValue * slotHeight
    const topPosition = headerHeight + proportion * totalHeight

    this.nowLineTarget.style.display = 'block'
    this.nowLineTarget.style.top = `${topPosition}px`
  }
}

stimulus.register('kanban-board', KanbanBoardController)
